import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useCurrency } from "@/context/CurrencyContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DollarSign, Package, ShoppingBag, TrendingUp, Wallet, Plus, Trash2, Loader2, CheckCircle2, Circle, Store, Gift, Share2, Camera, Copy, Users, ArrowUpRight, RotateCcw } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, AreaChart, Area } from "recharts";
import { toast } from "sonner";
import { ShipmentManager } from "@/components/ShipmentManager";
import { SellerShell } from "@/components/seller/SellerShell";
import { ProductForm } from "@/components/seller/ProductForm";
import { ProductsManager } from "@/components/seller/ProductsManager";
import { OrdersManager } from "@/components/seller/sections/OrdersManager";
import { InventoryManager } from "@/components/seller/sections/InventoryManager";
import { CustomersManager } from "@/components/seller/sections/CustomersManager";
import { WalletPayouts } from "@/components/seller/sections/WalletPayouts";
import { SubscriptionPlans } from "@/components/seller/sections/SubscriptionPlans";
import { SellerStateAnalytics } from "@/components/seller/SellerStateAnalytics";
import { AgeVerificationManager } from "@/components/AgeVerificationManager";
import { MessagesInbox } from "@/components/chat/MessagesInbox";
import { AdminSectionSkeleton } from "@/components/admin/AdminSkeletons";

const emptyProd = { title: "", description: "", category: "foodstuffs", country: "Nigeria", price: "", discount_price: "", shipping_fee: "", stock: "", sku: "", images: [], flash_sale: false };

export default function SellerDashboard() {
  const { user, logout, refresh } = useAuth();
  const { t } = useI18n();
  const { format, currency = "EUR", rates = { EUR: 1 }, currencies = [] } = useCurrency();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const section = params.get("section") || "dashboard";
  const setSection = (s) => setParams({ section: s });

  const [data, setData] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [pfOpen, setPfOpen] = useState(false);
  const [pfEditing, setPfEditing] = useState(null);
  const [wdAmount, setWdAmount] = useState("");
  const [shipFee, setShipFee] = useState("");
  const [shipCur, setShipCur] = useState("EUR");
  const [handlingDays, setHandlingDays] = useState("");
  const [shippingDays, setShippingDays] = useState("");
  const [orders, setOrders] = useState([]);
  const [cats, setCats] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [search, setSearch] = useState("");
  const [payout, setPayout] = useState({ bank_name: "", bank_code: "", account_number: "", account_name: "" });
  const [shop, setShop] = useState({ business_name: "", phone: "", city: "", shop_description: "", avatar: "", sell_currency: "EUR" });
  const [profile, setProfile] = useState({ name: "", city: "" });

  // Currency conversion helpers (all prices stored in EUR base).
  const displayFromEur = useCallback((eur, cur) => {
    const val = (parseFloat(eur) || 0) * (rates[cur] || 1);
    return cur === "NGN" ? String(Math.round(val)) : String(Number(val.toFixed(2)));
  }, [rates]);
  const toEur = useCallback((v, cur) => (parseFloat(v) || 0) / (rates[cur] || 1), [rates]);
  const shipSymbol = currencies.find((c) => c.code === shipCur)?.symbol || "€";

  const load = useCallback(() => {
    api.get("/seller/dashboard").then((r) => {
      const cur = r.data.sell_currency || "EUR";
      setData(r.data);
      setShipCur(cur);
      setShipFee(cur === "NGN" ? String(Math.round((r.data.shipping_fee ?? 0) * (rates[cur] || 1))) : String(Number(((r.data.shipping_fee ?? 0) * (rates[cur] || 1)).toFixed(2))));
      setHandlingDays(r.data.handling_days != null ? String(r.data.handling_days) : "");
      setShippingDays(r.data.shipping_days != null ? String(r.data.shipping_days) : "");
      const s = r.data.shop || {};
      setShop({ business_name: s.business_name || "", phone: s.phone || "", city: s.city || "", shop_description: s.shop_description || "", avatar: s.avatar || "", sell_currency: cur });
      setProfile({ name: s.name || "", city: s.city || "" });
    }).catch(() => {});
    api.get("/seller/wallet").then((r) => {
      setWallet(r.data);
      const p = r.data.payout || {};
      setPayout({ bank_name: p.bank_name || "", bank_code: p.bank_code || "", account_number: p.account_number || "", account_name: p.account_name || "" });
    }).catch(() => {});
    api.get("/seller/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  }, [rates]);
  useEffect(() => { load(); }, [load]);

  // Phase 3: live KPIs — refresh dashboard data as orders come in
  useEffect(() => {
    const iv = setInterval(() => {
      api.get("/seller/dashboard").then((r) => setData(r.data)).catch(() => {});
      api.get("/seller/orders").then((r) => setOrders(r.data)).catch(() => {});
    }, 20000);
    return () => clearInterval(iv);
  }, []);

  const openCreate = () => { setPfEditing(null); setPfOpen(true); };
  const openEdit = (p) => { setPfEditing(p); setPfOpen(true); };

  const referralLink = data?.referral?.code ? `${window.location.origin}/register?ref=${data.referral.code}` : "";

  const uploadAvatar = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setShop((s) => ({ ...s, avatar: reader.result }));
    reader.readAsDataURL(file);
  };
  const saveShop = async () => { try { await api.put("/seller/shop-settings", shop); toast.success("Shop info saved"); refresh?.(); load(); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); } };
  const saveProfile = async () => { try { await api.put("/me/profile", profile); toast.success("Profile saved"); refresh?.(); load(); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); } };
  const shareShop = async () => { if (referralLink) { try { await navigator.clipboard.writeText(referralLink); } catch { /* ignore */ } } try { await api.post("/seller/mark-shared"); } catch { /* ignore */ } toast.success("Referral link copied — share it to grow your shop!"); load(); };
  const copyReferral = async () => { try { await navigator.clipboard.writeText(referralLink); toast.success("Referral link copied"); } catch { toast.error("Copy failed"); } };
  const deleteAccount = async () => { if (!window.confirm("Delete your account? Your shop will be hidden and you'll be signed out. Contact support to restore it.")) return; try { await api.post("/me/delete"); toast.success("Account deleted"); logout(); navigate("/"); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); } };

  const delProduct = async (id) => { if (!window.confirm("Delete this product?")) return; await api.delete(`/products/${id}`); toast.success("Deleted"); load(); };
  const withdraw = async () => { const amtEur = (parseFloat(wdAmount) || 0) / (rates[currency] || 1); try { await api.post("/seller/withdraw", { amount: Number(amtEur.toFixed(2)) }); toast.success("Withdrawal requested"); setWdAmount(""); load(); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); } };
  const changeShipCur = (newCur) => { const eur = toEur(shipFee, shipCur); setShipFee(displayFromEur(eur, newCur)); setShipCur(newCur); };
  const saveShipping = async () => { const feeEur = Number(toEur(shipFee, shipCur).toFixed(2)); if (isNaN(feeEur) || feeEur < 0) return toast.error("Enter a valid shipping fee"); try { const body = { shipping_fee: feeEur }; if (handlingDays !== "") body.handling_days = parseInt(handlingDays, 10); if (shippingDays !== "") body.shipping_days = parseInt(shippingDays, 10); await api.put("/seller/shipping", body); toast.success("Store shipping settings updated"); load(); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); } };
  const savePayout = async () => { if (!payout.bank_name || !payout.bank_code || !payout.account_number || !payout.account_name) return toast.error("Fill in all payout fields"); try { await api.put("/seller/payout-details", payout); toast.success("Payout details saved"); load(); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); } };

  const customers = useMemo(() => {
    const map = {};
    orders.forEach((o) => {
      const id = o.user_id || "unknown";
      if (!map[id]) map[id] = { id, orders: 0, spent: 0, last: o.created_at };
      map[id].orders += 1;
      map[id].spent += (o.items || []).reduce((s, it) => s + (it.price || 0) * (it.quantity || 1), 0);
      if (o.created_at > map[id].last) map[id].last = o.created_at;
    });
    return Object.values(map).sort((a, b) => b.spent - a.spent);
  }, [orders]);

  const AddProductBtn = () => (
    <Button data-testid="add-product-btn" className="rounded-full" onClick={openCreate}><Plus className="w-4 h-4 mr-1" />{t("addProduct")}</Button>
  );

  const notApproved = user?.seller_status !== "approved" && user?.role !== "admin";
  if (notApproved) {
    const st = user?.seller_status;
    const info = st === "pending"
      ? { title: "Application under review", msg: "Thanks for applying! Our team is verifying your documents. You'll be notified once your store is approved.", cta: false }
      : (st === "suspended" || user?.subscription_status === "cancelled")
      ? { title: "Store deactivated", msg: "Your store is currently paused/deactivated. Please contact our admin team to reactivate it.", cta: false }
      : st === "rejected"
      ? { title: "Application not approved", msg: user?.rejection_reason ? `Reason: ${user.rejection_reason}` : "Your seller application was not approved. Update your details and re-apply below.", cta: true, ctaLabel: "Update & Re-apply" }
      : { title: t("sellerAccessPending"), msg: t("completeApplication"), cta: true, ctaLabel: t("applyNow") };
    return (
    <SellerShell active={section} onNavigate={setSection} search={search} onSearch={setSearch} notifications={notifs}>
      <div className="max-w-lg mx-auto text-center py-24" data-testid="seller-status-gate">
        <h1 className="font-display font-bold text-2xl">{info.title}</h1>
        <p className="text-muted-foreground mt-2">{info.msg}</p>
        {st === "pending" && <p className="mt-3 inline-block rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold px-3 py-1.5" data-testid="sla-badge">⏱ Most stores are reviewed within 1–2 business days</p>}
        {info.cta && <Link to="/seller/apply"><Button className="mt-4 rounded-full">{info.ctaLabel}</Button></Link>}
      </div>
    </SellerShell>
    );
  }
  if (!data) return (
    <SellerShell active={section} onNavigate={setSection} search={search} onSearch={setSearch} notifications={notifs}>
      <AdminSectionSkeleton />
    </SellerShell>
  );

  const meta = {
    dashboard: { title: t("sellerDashboardTitle"), subtitle: user?.business_name || t("yourStore"), actions: <AddProductBtn /> },
    products: { title: t("productsLabel"), subtitle: `${data.total_products} products`, actions: <AddProductBtn /> },
    inventory: { title: "Inventory", subtitle: "Stock levels & restock alerts" },
    orders: { title: "Orders", subtitle: `${orders.length} paid orders` },
    messages: { title: "Messages", subtitle: "Chat with your buyers in real time" },
    payments: { title: "Payments", subtitle: "Wallet, earnings & payouts" },
    customers: { title: "Customers", subtitle: `${customers.length} customers` },
    analytics: { title: t("analytics"), subtitle: "Sales performance" },
    settings: { title: "Settings", subtitle: "Shop, profile & preferences" },
  }[section] || {};

  const stats = [
    { icon: Wallet, label: t("walletBalance"), value: format(data.wallet_balance), color: "text-green-600" },
    { icon: TrendingUp, label: t("netRevenue"), value: format(data.revenue), color: "text-primary" },
    { icon: ShoppingBag, label: t("ordersLabel"), value: data.total_orders, color: "text-secondary" },
    { icon: Package, label: t("productsLabel"), value: data.total_products, color: "text-gold" },
  ];

  const markNotifsRead = () => setNotifs((ns) => ns.map((n) => ({ ...n, read: true })));

  return (
    <SellerShell active={section} onNavigate={setSection} search={search} onSearch={setSearch} notifications={notifs} onReadNotifications={markNotifsRead} title={meta.title} subtitle={meta.subtitle} actions={meta.actions}>
      {/* DASHBOARD */}
      {section === "dashboard" && (
        <div className="space-y-6">
          {data.subscription && (
            <Card className={`p-4 flex flex-wrap items-center gap-3 ${data.subscription.on_trial ? "border-green-300 bg-green-50 dark:bg-green-950/20" : "border-amber-300 bg-amber-50 dark:bg-amber-950/20"}`} data-testid="seller-plan-banner">
              <Gift className={`w-5 h-5 shrink-0 ${data.subscription.on_trial ? "text-green-600" : "text-amber-600"}`} />
              <div className="flex-1 min-w-[200px]">
                {data.subscription.on_trial ? (
                  <p className="text-sm"><span className="font-semibold">Free trial active</span> — {data.subscription.trial_days_left} day{data.subscription.trial_days_left !== 1 ? "s" : ""} left. You pay <span className="font-semibold">0% commission</span> until then.</p>
                ) : (
                  <p className="text-sm"><span className="font-semibold">Active plan</span> — platform commission is <span className="font-semibold">{data.subscription.commission_pct}%</span> per sale.</p>
                )}
              </div>
              <Badge variant="outline" className="capitalize" data-testid="seller-plan-status">{data.subscription.status}</Badge>
            </Card>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((s) => (
              <Card key={s.label} className="p-4"><s.icon className={`w-5 h-5 ${s.color} mb-2`} /><p className="text-2xl font-black font-display">{s.value}</p><p className="text-xs text-muted-foreground">{s.label}</p></Card>
            ))}
          </div>

          {data.returns && (
            <Card className="p-4" data-testid="seller-return-rate-card">
              <div className="flex items-center gap-4">
                <span className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${data.returns.rate > 10 ? "bg-red-100 text-red-600" : data.returns.rate > 5 ? "bg-amber-100 text-amber-600" : "bg-green-100 text-green-600"}`}>
                  <RotateCcw className="w-5 h-5" />
                </span>
                <div className="flex-1">
                  <p className="text-sm font-semibold">Return rate <span className={data.returns.rate > 10 ? "text-red-600" : data.returns.rate > 5 ? "text-amber-600" : "text-green-600"}>{data.returns.rate}%</span></p>
                  <p className="text-xs text-muted-foreground">{data.returns.count} return{data.returns.count !== 1 ? "s" : ""} across your paid orders. {data.returns.rate > 10 ? "High — review product quality & descriptions." : data.returns.rate > 5 ? "Keep an eye on recurring reasons." : "Healthy — great job keeping buyers happy!"}</p>
                </div>
              </div>
              {data.returns.reasons?.length > 0 && (
                <div className="mt-4 pt-3 border-t border-border/50">
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">Top return reasons</p>
                  <div className="space-y-1.5" data-testid="seller-return-reasons">
                    {data.returns.reasons.map((r, i) => {
                      const pct = Math.round((r.count / data.returns.count) * 100);
                      return (
                        <div key={i} className="flex items-center gap-3 text-xs">
                          <span className="w-40 truncate shrink-0">{r.reason}</span>
                          <span className="flex-1 h-2 rounded-full bg-muted overflow-hidden"><span className="block h-full bg-primary rounded-full" style={{ width: `${pct}%` }} /></span>
                          <span className="w-14 text-right text-muted-foreground shrink-0">{r.count} · {pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </Card>
          )}

          {data.onboarding && !Object.values(data.onboarding).every(Boolean) && (
            <Card className="p-5" data-testid="onboarding-checklist">
              <h3 className="font-semibold text-lg mb-1">Complete your shop</h3>
              <p className="text-sm text-muted-foreground mb-4">Finish these steps to grow your store.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { key: "shop_created", label: "Create your shop", action: null },
                  { key: "first_product", label: "Add your first product", action: openCreate },
                  { key: "profile_photo", label: "Add a profile photo", action: () => setSection("settings") },
                  { key: "shared_shop", label: "Share your shop", action: shareShop },
                ].map((step) => (
                  <button key={step.key} data-testid={`onboarding-${step.key}`} onClick={step.action || undefined} disabled={data.onboarding[step.key] || !step.action}
                    className={`flex items-center gap-2 p-3 rounded-xl border text-left text-sm transition-colors disabled:cursor-default ${data.onboarding[step.key] ? "bg-green-50 dark:bg-green-950/30 border-green-200 text-green-700 dark:text-green-400" : "hover:border-primary"}`}>
                    {data.onboarding[step.key] ? <CheckCircle2 className="w-5 h-5 shrink-0" /> : <Circle className="w-5 h-5 shrink-0 text-muted-foreground" />}
                    <span>{step.label}</span>
                  </button>
                ))}
              </div>
            </Card>
          )}

          <div className="grid lg:grid-cols-3 gap-6">
            <Card className="p-5 lg:col-span-2"><h3 className="font-semibold mb-4">{t("revenueOverTime")}</h3>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={data.sales_series}>
                  <defs><linearGradient id="sg" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.35} /><stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} /></linearGradient></defs>
                  <XAxis dataKey="date" fontSize={11} /><YAxis fontSize={11} /><Tooltip /><Area type="monotone" dataKey="amount" stroke="hsl(var(--primary))" fill="url(#sg)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
            <Card className="p-5">
              <h3 className="font-semibold mb-4 flex items-center justify-between">Recent orders <button onClick={() => setSection("orders")} className="text-xs text-primary font-medium flex items-center gap-0.5">View all <ArrowUpRight className="w-3 h-3" /></button></h3>
              <div className="space-y-2">
                {orders.slice(0, 5).map((o) => (
                  <div key={o.order_id} className="flex items-center justify-between text-sm border-b border-border/40 pb-2">
                    <span className="font-mono text-xs">#{o.order_id.slice(-8)}</span>
                    <Badge variant="outline" className="capitalize text-[10px]">{(o.status || "").replace("_", " ")}</Badge>
                  </div>
                ))}
                {orders.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No orders yet.</p>}
              </div>
            </Card>
          </div>
          <SellerStateAnalytics format={format} />
        </div>
      )}

      {/* PRODUCTS */}
      {section === "products" && (
        <ProductsManager products={data.products} format={format} search={search} onCreate={openCreate} onEdit={openEdit} onDelete={delProduct} />
      )}

      {/* INVENTORY */}
      {section === "inventory" && (
        <InventoryManager products={data.products} format={format} search={search} onUpdated={load} />
      )}

      {/* ORDERS */}
      {section === "orders" && (
        <OrdersManager orders={orders} format={format} search={search} onUpdated={load} />
      )}

      {/* MESSAGES */}
      {section === "messages" && (
        <MessagesInbox testid="seller-messages" emptyHint="No buyer messages yet. When a buyer contacts you about a product, it'll show up here." />
      )}

      {/* PAYMENTS */}
      {section === "payments" && (
        <WalletPayouts
          wallet={wallet} format={format}
          currency={currency} rate={rates[currency] || 1}
          currencySymbol={currencies.find((c) => c.code === currency)?.symbol || "€"}
          wdAmount={wdAmount} setWdAmount={setWdAmount} onWithdraw={withdraw}
          payout={payout} setPayout={setPayout} onSavePayout={savePayout}
        />
      )}

      {/* CUSTOMERS */}
      {section === "customers" && (
        <CustomersManager orders={orders} format={format} search={search} />
      )}

      {/* AGE VERIFICATION */}
      {section === "age" && (
        <AgeVerificationManager />
      )}

      {/* SUBSCRIPTION */}
      {section === "subscription" && (
        <SubscriptionPlans subscription={data.subscription} onChanged={load} />
      )}

      {/* ANALYTICS */}
      {section === "analytics" && (
        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="p-5"><h3 className="font-semibold mb-4">{t("revenueOverTime")}</h3>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data.sales_series}><XAxis dataKey="date" fontSize={11} /><YAxis fontSize={11} /><Tooltip /><Line type="monotone" dataKey="amount" stroke="hsl(var(--primary))" strokeWidth={2} /></LineChart>
            </ResponsiveContainer>
          </Card>
          <Card className="p-5"><h3 className="font-semibold mb-4">{t("salesLabel")}</h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.sales_series}><XAxis dataKey="date" fontSize={11} /><YAxis fontSize={11} /><Tooltip /><Bar dataKey="amount" fill="hsl(var(--secondary))" radius={[6, 6, 0, 0]} /></BarChart>
            </ResponsiveContainer>
            <p className="text-xs text-muted-foreground mt-3">{t("commissionPaid")}: {format(data.commission_paid)} (2.5%)</p>
          </Card>
        </div>
      )}

      {/* SETTINGS */}
      {section === "settings" && (
        <div className="grid lg:grid-cols-2 gap-6 items-start">
          <Card className="p-6" data-testid="shop-settings">
            <h3 className="font-semibold text-lg mb-4">Edit your shop info</h3>
            <div className="flex items-center gap-4 mb-5">
              <div className="w-20 h-20 rounded-full bg-muted overflow-hidden flex items-center justify-center shrink-0">
                {shop.avatar ? <img src={shop.avatar} alt="" className="w-full h-full object-cover" /> : <Store className="w-8 h-8 text-muted-foreground" />}
              </div>
              <label className="flex items-center gap-2 px-4 py-2 border border-dashed rounded-lg cursor-pointer hover:border-primary text-sm">
                <Camera className="w-4 h-4" />Profile photo
                <input type="file" data-testid="shop-avatar-upload" accept="image/*" onChange={uploadAvatar} className="hidden" />
              </label>
            </div>
            <div className="space-y-3">
              <div><Label className="text-xs">Shop name</Label><Input data-testid="shop-name" value={shop.business_name} onChange={(e) => setShop({ ...shop, business_name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Phone number</Label><Input data-testid="shop-phone" value={shop.phone} onChange={(e) => setShop({ ...shop, phone: e.target.value })} /></div>
                <div><Label className="text-xs">City</Label><Input data-testid="shop-city" value={shop.city} onChange={(e) => setShop({ ...shop, city: e.target.value })} /></div>
              </div>
              <div><Label className="text-xs">Description</Label><Textarea data-testid="shop-description" value={shop.shop_description} onChange={(e) => setShop({ ...shop, shop_description: e.target.value })} placeholder="Tell buyers about your shop" /></div>
              <div>
                <Label className="text-xs">Selling currency</Label>
                <Select value={shop.sell_currency} onValueChange={(v) => setShop({ ...shop, sell_currency: v })}>
                  <SelectTrigger data-testid="sell-currency-select" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(currencies.length ? currencies : [{ code: "EUR", symbol: "€", name: "Euro" }, { code: "USD", symbol: "$", name: "US Dollar" }, { code: "NGN", symbol: "₦", name: "Naira" }]).map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code} — {c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">You enter product & shipping prices in this currency. Buyers still see prices in their own currency.</p>
              </div>
              <Button data-testid="save-shop-btn" onClick={saveShop} className="rounded-full">Save shop info</Button>
            </div>
          </Card>

          <Card className="p-6" data-testid="shipping-settings">
            <h3 className="font-semibold text-lg">Store shipping fee</h3>
            <p className="text-sm text-muted-foreground mt-1 mb-4">A flat fee used when a product has no shipping price. Set it to <strong>0</strong> for free shipping.</p>
            <div className="flex items-center justify-between gap-2 mb-3">
              <Label className="text-sm font-medium" htmlFor="ship-fee">Shipping fee ({shipCur})</Label>
              <Select value={shipCur} onValueChange={changeShipCur}>
                <SelectTrigger data-testid="shipping-currency-select" className="h-8 w-28"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(currencies.length ? currencies : [{ code: "EUR", symbol: "€" }, { code: "USD", symbol: "$" }, { code: "NGN", symbol: "₦" }]).map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 mt-1.5">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">{shipSymbol}</span>
                <Input id="ship-fee" data-testid="shipping-fee-input" type="number" min="0" step="0.01" className="pl-7" value={shipFee} onChange={(e) => setShipFee(e.target.value)} />
              </div>
              <Button data-testid="save-shipping-btn" onClick={saveShipping} className="rounded-full">Save</Button>
            </div>
            {shipCur !== "EUR" && shipFee !== "" && <p className="text-[11px] text-muted-foreground mt-1" data-testid="shipping-fee-eur">≈ €{toEur(shipFee, shipCur).toFixed(2)} stored</p>}
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div>
                <Label className="text-sm font-medium" htmlFor="handling-days">Handling time (days)</Label>
                <Input id="handling-days" data-testid="handling-days-input" type="number" min="0" max="30" className="mt-1.5" placeholder="e.g. 1" value={handlingDays} onChange={(e) => setHandlingDays(e.target.value)} />
              </div>
              <div>
                <Label className="text-sm font-medium" htmlFor="shipping-days">Shipping time (days)</Label>
                <Input id="shipping-days" data-testid="shipping-days-input" type="number" min="1" max="60" className="mt-1.5" placeholder="e.g. 4" value={shippingDays} onChange={(e) => setShippingDays(e.target.value)} />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Buyers see your estimated delivery date in the cart based on these times. Leave blank to use the marketplace default.</p>
            <p className="text-xs text-muted-foreground mt-3">Current: <span className="font-semibold text-foreground" data-testid="current-shipping-fee">{Number(data.shipping_fee || 0) > 0 ? `€${Number(data.shipping_fee).toFixed(2)}` : "Free shipping"}</span>{(data.handling_days != null || data.shipping_days != null) && <span> · ETA {Number(data.handling_days ?? 1) + Number(data.shipping_days ?? 4)}–{Number(data.handling_days ?? 1) + Number(data.shipping_days ?? 4) + 2} days</span>}</p>
          </Card>

          <Card className="p-6" data-testid="profile-settings">
            <h3 className="font-semibold text-lg mb-4">Edit my profile</h3>
            <div className="space-y-3">
              <div><Label className="text-xs">Name</Label><Input data-testid="profile-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} /></div>
              <div><Label className="text-xs">City</Label><Input data-testid="profile-city" value={profile.city} onChange={(e) => setProfile({ ...profile, city: e.target.value })} /></div>
              <div className="flex gap-2"><Button data-testid="save-profile-btn" onClick={saveProfile} className="rounded-full">Save</Button><Button variant="outline" onClick={() => load()} className="rounded-full">Cancel</Button></div>
            </div>
          </Card>

          <Card className="p-6" data-testid="referral-card">
            <h3 className="font-semibold text-lg flex items-center gap-2"><Gift className="w-5 h-5 text-primary" />Referral program</h3>
            <p className="text-sm text-muted-foreground mt-1 mb-4">Share your link. You've referred <strong data-testid="referral-count">{data.referral?.count ?? 0}</strong> {(data.referral?.count === 1) ? "person" : "people"}.</p>
            <div className="flex gap-2"><Input data-testid="referral-link" readOnly value={referralLink} className="text-xs" /><Button data-testid="copy-referral-btn" onClick={copyReferral} variant="outline" className="rounded-full shrink-0"><Copy className="w-4 h-4 mr-1" />Copy</Button></div>
            <Button data-testid="share-shop-btn" onClick={shareShop} className="rounded-full mt-3"><Share2 className="w-4 h-4 mr-1" />Share my shop</Button>
          </Card>

          <Card className="p-6 border-destructive/40 lg:col-span-2" data-testid="advanced-settings">
            <h3 className="font-semibold text-lg text-destructive">Advanced settings</h3>
            <p className="text-sm text-muted-foreground mt-1 mb-4">Deleting your account hides your shop and signs you out. Contact support to restore it.</p>
            <Button data-testid="delete-account-btn" variant="destructive" onClick={deleteAccount} className="rounded-full"><Trash2 className="w-4 h-4 mr-1" />Delete my account</Button>
          </Card>
        </div>
      )}
      <ProductForm open={pfOpen} onOpenChange={setPfOpen} product={pfEditing} cats={cats} sellCurrency={shop.sell_currency} onSaved={() => { setPfOpen(false); load(); }} />
    </SellerShell>
  );
}
